import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link, SwitchButton, StatusTooltip } from 'components/common';
import { localize } from 'core/localization';
import { BUTTON, LINK, INPUT_TYPE, SIZES, TOOLTIP } from 'constants/components';
import { getEmail, getName, shouldSaveCrossDevice } from 'store/user/selectors';
import progressStorage from 'core/progressStorage';
import { STATUS_OK } from 'constants/progressStorage';
import Input from '../Input';
import {
  Form,
  ButtonSubmit,
  InputContainer,
  ControlContainer,
  MessageText,
  CheckboxContainer,
  LinkWrapper,
  MessageTextWrapper
} from './LoginForm.styled';

export class LoginForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      name: '',
      password: '',
      shortTermAccess: true,
      validationStatusBeforeSubmit: {
        name: true,
        email: true,
        password: true
      }
    };
    this.refName = '';
    this.refEmail = '';
    this.refPass = '';
  }

  componentDidMount() {
    let { userEmail, userName } = this.props;

    if (userEmail || userName) {
      this.setState({
        email: userEmail || '',
        name: userName || ''
      });
    }
  }

  onChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  hideMessage = () => {
    this.setState({ resetPasswordSendMessage: false });
  };

  beforeSubmitValidation = (event, onSubmit) => {
    event.preventDefault();
    const { saveCrossDevice } = this.props;
    const nameValid = this.refName ? this.refName.validity.valid : false;
    const emailValid = this.refEmail ? this.refEmail.validity.valid : false;
    const passwordValid = this.refPass ? this.refPass.validity.valid : false;

    this.setState({
      validationStatusBeforeSubmit: {
        name: nameValid,
        email: emailValid,
        password: passwordValid
      },
      resetPasswordSendMessage: false
    });
    if (emailValid && ((passwordValid && saveCrossDevice) || (nameValid && !saveCrossDevice))) {
      onSubmit(event, this.state);
    }
  };

  onPasswordRestore = async () => {
    const emailValid = this.refEmail ? this.refEmail.validity.valid : false;
    this.setState({ validationStatusBeforeSubmit: { email: emailValid } });

    if (!emailValid) {
      return;
    }

    const { email } = this.state;
    let status = await progressStorage.resetPassword(email);
    this.setState({ resetPasswordSendMessage: status === STATUS_OK });
  };

  render() {
    const { shortTermAccess } = this.state;
    const {
      onSubmit,
      formId,
      userEmail,
      userName,
      saveCrossDevice,
      isEmailPasswordCombinationNotExists,
      resetStatuses
    } = this.props;
    return (
      <Form
        idElement={formId}
        onSubmit={event => {
          this.beforeSubmitValidation(event, onSubmit, saveCrossDevice);
        }}
        noValidate
      >
        <InputContainer size={40}>
          <Input
            idInput="email"
            type={INPUT_TYPE.EMAIL}
            refInput={node => {
              this.refEmail = node;
            }}
            name="email"
            labelText="Email"
            defaultValue={userEmail}
            placeholder="john@example.com"
            autoComplete="new-email"
            onChange={this.onChange}
            validIcon={true}
            textError={localize('[error email input]')}
            validationStatusBeforeSubmit={this.state.validationStatusBeforeSubmit.email}
          />
        </InputContainer>
        {!saveCrossDevice && (
          <InputContainer size={40}>
            <Input
              idInput="name"
              type={INPUT_TYPE.TEXT}
              refInput={node => {
                this.refName = node;
              }}
              name="name"
              labelText="Name"
              defaultValue={userName}
              placeholder="John Doe"
              onChange={this.onChange}
              validIcon={true}
              pattern="(:?[A-Za-zА-Яа-яЁё]+|\s*)+"
              validationStatusBeforeSubmit={this.state.validationStatusBeforeSubmit.name}
            />
          </InputContainer>
        )}
        {saveCrossDevice && (
          <InputContainer size={17}>
            <Input
              idInput="password"
              type={INPUT_TYPE.PASSWORD}
              refInput={node => {
                this.refPass = node;
              }}
              name="password"
              placeholder="Password"
              labelText="Password"
              autoComplete="new-password"
              onChange={this.onChange}
              showValidationMessage={false}
              pattern="\S+"
              validationStatusBeforeSubmit={this.state.validationStatusBeforeSubmit.password}
            />
            <ControlContainer>
              {this.renderWithTooltip(
                TOOLTIP.CORRECT,
                this.state.resetPasswordSendMessage,
                localize('[reset password link sent success]'),
                <LinkWrapper>
                  <Link
                    text={localize('[forgot pass]')}
                    layout={LINK.LAYOUT.FORM}
                    size={11}
                    onClick={this.onPasswordRestore}
                    onBlur={this.hideMessage}
                  />
                </LinkWrapper>
              )}
              <CheckboxContainer>
                <SwitchButton
                  multiple={true}
                  text={localize('[remember me]')}
                  selected={!shortTermAccess}
                  aria-checked={!shortTermAccess}
                  size={SIZES.SMALL}
                  tabIndex={0}
                  onClick={() => this.setState({ shortTermAccess: !shortTermAccess })}
                  onKeyDown={event => {
                    if (event.key === ` ` || event.key === 'Enter') {
                      this.setState({ shortTermAccess: !shortTermAccess });
                    }
                  }}
                />
              </CheckboxContainer>
            </ControlContainer>
          </InputContainer>
        )}
        {this.renderWithTooltip(
          TOOLTIP.INCORRECT,
          isEmailPasswordCombinationNotExists,
          <MessageTextWrapper>
            <MessageText appearance="span">{localize('[error login email]')}</MessageText>
            <MessageText appearance="span">{localize('[error login pass]')}</MessageText>
          </MessageTextWrapper>,
          <ButtonSubmit
            layout={BUTTON.LAYOUT.PRIMARY}
            suffixIcon="arrow-right"
            type="submit"
            onBlur={() => resetStatuses()}
          >
            {localize('[login button]')}
          </ButtonSubmit>
        )}
      </Form>
    );
  }

  renderWithTooltip = (status, showTooltip, message, actionElement) => (
    <StatusTooltip
      popoverPosition={['top']}
      popoverAlign={'center'}
      layout={status}
      showTooltip={showTooltip}
      message={message}
      popoverButton={actionElement}
      transitionDuration={0.2}
      eventHandler={'click'}
    />
  );
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  formId: PropTypes.string,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  saveCrossDevice: PropTypes.bool.isRequired,
  isEmailPasswordCombinationNotExists: PropTypes.bool,
  resetStatuses: PropTypes.func
};

LoginForm.defaultProps = {
  isEmailPasswordCombinationNotExists: false
};

function mapStateToProps(state) {
  return {
    userEmail: getEmail(state),
    userName: getName(state),
    saveCrossDevice: shouldSaveCrossDevice(state)
  };
}

export default connect(mapStateToProps)(LoginForm);
